/**
 * Registry анимированных иконок (Lottie) для чатов.
 *
 * Storage:
 *   chat_topics.icon хранит одно из:
 *     - emoji ("🔥")
 *     - "lottie:<id>"     — ссылка на запись из ANIMATED_ICONS ниже
 *     - "lottieurl:<url>" — произвольная Lottie URL, выбранная юзером
 *
 * Где брать Lottie-анимации:
 *   - https://lottiefiles.com/free-animated-icons  (фильтр Free + Lottie format)
 *   - Жмёшь "Get Lottie URL" → копируешь, добавляешь сюда
 *   - Или вставляешь URL прямо в picker через таб "Custom URL"
 *
 * Чтобы добавить новую иконку в библиотеку — просто допиши запись.
 */

export type AnimatedIconCategory = "popular" | "objects" | "nature" | "activity";

export interface AnimatedIcon {
  id: string;
  name: string;
  category: AnimatedIconCategory;
  url: string;            // URL к Lottie JSON
  fallback: string;       // emoji-fallback пока загружается / если URL мёртв
}

/**
 * СТАРТОВЫЙ ПАК — пустой, потому что нет общеизвестного бесплатного источника
 * Lottie-анимаций с гарантированно стабильными URL'ами. Заполнить вручную:
 *   1. Выбрать анимацию на https://lottiefiles.com/free-animated-icons
 *   2. Скачать JSON или взять прямую URL из "Get Lottie URL"
 *   3. Добавить запись { id, name, category, url, fallback }
 *
 * Альтернатива: положить JSON-файлы в `frontend/public/lottie/<id>.json` и
 * указать `url: "/lottie/<id>.json"` — тогда зависимости от внешнего CDN нет.
 */
export const ANIMATED_ICONS: AnimatedIcon[] = [
  // Стартовый набор — проверенные публичные LottieFiles URL (HTTP 200).
  // Если URL внезапно перестанет работать — fallback emoji подменит лоадер.
  // Чтобы расширить — найди анимацию на https://lottiefiles.com/free-animated-icons
  // и добавь запись.
  { id: "party",      name: "Конфетти",     category: "popular",  fallback: "🎉",
    url: "https://assets3.lottiefiles.com/packages/lf20_obhph3sh.json" },
  { id: "trophy",     name: "Кубок",        category: "popular",  fallback: "🏆",
    url: "https://assets3.lottiefiles.com/packages/lf20_touohxv0.json" },
  { id: "wave",       name: "Привет",       category: "popular",  fallback: "👋",
    url: "https://assets3.lottiefiles.com/packages/lf20_V9t630.json" },
  { id: "success",    name: "Успех",        category: "popular",  fallback: "✅",
    url: "https://assets3.lottiefiles.com/packages/lf20_jbrw3hcz.json" },
  { id: "entertainment", name: "Развлечения", category: "popular", fallback: "🎭",
    url: "https://assets3.lottiefiles.com/packages/lf20_cbrbre30.json" },
  { id: "plane",      name: "Самолётик",    category: "objects",  fallback: "✈️",
    url: "https://assets3.lottiefiles.com/packages/lf20_x62chJ.json" },
  { id: "camera",     name: "Камера",       category: "objects",  fallback: "📸",
    url: "https://assets3.lottiefiles.com/packages/lf20_jhlaooj5.json" },
  { id: "loading",    name: "Загрузка",     category: "objects",  fallback: "⏳",
    url: "https://assets3.lottiefiles.com/packages/lf20_uwR49r.json" },
  { id: "spinner",    name: "Спиннер",      category: "objects",  fallback: "⚙️",
    url: "https://assets3.lottiefiles.com/packages/lf20_rwq6ciql.json" },
  { id: "news",       name: "Новости",      category: "activity", fallback: "📰",
    url: "https://assets3.lottiefiles.com/packages/lf20_qmfs6c3i.json" },
  { id: "doctor",     name: "Врач",         category: "activity", fallback: "👨‍⚕️",
    url: "https://assets3.lottiefiles.com/packages/lf20_tutvdkg0.json" },
  { id: "clapper",    name: "Клапер",       category: "activity", fallback: "🎬",
    url: "https://assets3.lottiefiles.com/packages/lf20_khzniaya.json" },
  { id: "wavy",       name: "Волна",        category: "nature",   fallback: "〰️",
    url: "https://assets3.lottiefiles.com/packages/lf20_xlmz9xwm.json" },
];

export const ANIMATED_ICONS_BY_ID: Record<string, AnimatedIcon> =
  Object.fromEntries(ANIMATED_ICONS.map(i => [i.id, i]));


/** Пак 3D-emoji + строительных иконок — статические PNG/WEBP в
 *  /public/icons/. Хранится в chat_topics.icon как "pack:N" где N от 1
 *  до PACK_ICON_COUNT. Иконки 1..100 — оригинальный пак (бизнес/коммуникация
 *  /деньги), иконки 101..254 — пак для «Кирпичных беседок» (стройка /
 *  транспорт / материалы / инструменты / металлопрокат / печи / беседки), 255..296 — фиолетовый пак BrixChat
 *  (06.10.2026: значки групповых чатов и «Избранного», в стиле карточек приложений). */
export const PACK_ICON_COUNT = 296;
// WebP — ~5KB вместо ~22KB PNG (4× меньше). Все современные браузеры включая
// iOS 14+/Safari 14+ поддерживают. PNG-fallback лежит рядом если нужен.
export const packIconUrl = (n: number): string => `/icons/icon-${n}.webp`;

/** Распарсить icon-строку из БД. */
export type TopicIconParsed =
  | { kind: "lottie"; lottie: AnimatedIcon }
  | { kind: "lottie_url"; url: string }
  | { kind: "pack"; index: number; url: string }
  | { kind: "emoji"; emoji: string }
  | { kind: "none" };

export function parseTopicIcon(icon: string | null | undefined): TopicIconParsed {
  if (!icon) return { kind: "none" };
  if (icon.startsWith("lottie:")) {
    const id = icon.slice("lottie:".length);
    const lottie = ANIMATED_ICONS_BY_ID[id];
    if (lottie) return { kind: "lottie", lottie };
    return { kind: "none" };
  }
  if (icon.startsWith("lottieurl:")) {
    const url = icon.slice("lottieurl:".length);
    return { kind: "lottie_url", url };
  }
  if (icon.startsWith("pack:")) {
    const n = parseInt(icon.slice("pack:".length), 10);
    if (Number.isFinite(n) && n >= 1 && n <= PACK_ICON_COUNT) {
      return { kind: "pack", index: n, url: packIconUrl(n) };
    }
    return { kind: "none" };
  }
  return { kind: "emoji", emoji: icon };
}
